import React from 'react';
import { Box, Typography, Button, Card, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import GradePill from './GradePill';
import Meter from './Meter';
import type { IntelligenceDriver } from '../../../types/intelligence';
import { getIntelligenceColors } from '../../../utils/intelligenceTheme';

interface DriverRowProps {
  driver: IntelligenceDriver;
}

export const DriverRow: React.FC<DriverRowProps> = React.memo(({ driver }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const intelColors = getIntelligenceColors(isDark);
  const { name, score, why, action } = driver;
  const deficit = 100 - score;

  // Use intelligence colors in dark mode, theme colors in light mode
  const cardBgColor = isDark
    ? intelColors!.background.paper
    : alpha(theme.palette.background.paper, 0.4);
  const cardBorderColor = isDark ? intelColors!.border.primary : theme.palette.divider;
  const cardHoverBg = isDark
    ? `${intelColors!.background.secondary}B3` // 70% opacity
    : alpha(theme.palette.background.paper, 0.7);
  const textPrimary = isDark ? intelColors!.text.primary : theme.palette.text.primary;
  const textSecondary = isDark ? intelColors!.text.muted : theme.palette.text.secondary;

  return (
    <Card
      sx={{
        backgroundColor: cardBgColor,
        border: `1px solid ${cardBorderColor}`,
        borderRadius: 3,
        p: 3,
        transition: 'all 0.3s ease',
        '&:hover': {
          backgroundColor: cardHoverBg,
          transform: 'translateY(-1px)',
          boxShadow: theme.shadows[4],
        },
      }}
    >
      {/* Header Row */}
      <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 3, mb: 2 }}>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textPrimary }}>
              {name}
            </Typography>
            <GradePill score={score} />
          </Box>
          <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.875rem' }}>
            {why}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: textPrimary, lineHeight: 1 }}>
            {Math.round(score)}
          </Typography>
          <Typography variant="caption" sx={{ color: textSecondary, fontSize: '0.7rem' }}>
            /100
          </Typography>
        </Box>
      </Box>

      {/* Progress Meter */}
      <Box sx={{ mb: 2 }}>
        <Meter score={score} />
      </Box>

      {/* Potential Impact */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="caption" sx={{ color: textSecondary }}>
          Potential lift if improved:
        </Typography>
        <Typography variant="caption" sx={{ color: textPrimary, fontWeight: 500 }}>
          +{Math.ceil(deficit * 0.2)} to overall*
        </Typography>
      </Box>

      {/* Action Button */}
      <Button
        variant="outlined"
        size="small"
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '0.75rem',
          py: 1.5,
          px: 2,
          whiteSpace: 'normal',
          textAlign: 'left',
          justifyContent: 'flex-start',
          lineHeight: 1.4,
          backgroundColor: isDark
            ? intelColors!.background.secondary
            : alpha(theme.palette.primary.main, 0.1),
          borderColor: isDark
            ? intelColors!.border.secondary
            : alpha(theme.palette.primary.main, 0.3),
          color: textPrimary,
          '&:hover': {
            backgroundColor: isDark
              ? intelColors!.border.primary
              : alpha(theme.palette.primary.main, 0.2),
            borderColor: isDark
              ? intelColors!.border.secondary
              : theme.palette.primary.main,
          },
        }}
        fullWidth
      >
        Suggested action: {action}
      </Button>
    </Card>
  );
});

export default DriverRow;